import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Formulario } from "@/types/formulario";
import { useClientFilter } from "./useClientFilter";

export function useFormularios() {
  const queryClient = useQueryClient();
  const { currentClientId, showAllClients, clientLoading } = useClientFilter();

  // Buscar todos os formulários
  const { data: formularios = [], isLoading: loading, error } = useQuery({
    queryKey: ['formularios', currentClientId, showAllClients],
    queryFn: async () => {
      let query = supabase
        .from('formularios')
        .select(`
          *,
          clients(id, name),
          formulario_clientes(client_id, clients(id, name))
        `);

      // Aplicar filtro de cliente usando formulario_clientes
      if (currentClientId && !showAllClients) {
        // Get formulario IDs that match the client filter
        const { data: matchingIds } = await supabase
          .from('formulario_clientes')
          .select('formulario_id')
          .eq('client_id', currentClientId);

        if (matchingIds && matchingIds.length > 0) {
          query = query.in('id', matchingIds.map((m: any) => m.formulario_id));
        } else {
          return [];
        }
      }

      const { data, error } = await query.order('criado_em', { ascending: false });

      if (error) throw error;

      // Buscar contagens de campos e respostas para cada formulário
      const formulariosComContagens = await Promise.all(
        (data || []).map(async (form: any) => {
          const [camposCount, respostasCount] = await Promise.all([
            supabase
              .from('formulario_campos')
              .select('id', { count: 'exact', head: true })
              .eq('formulario_id', form.id)
              .eq('ativo', true),
            supabase
              .from('formulario_respostas')
              .select('id', { count: 'exact', head: true })
              .eq('formulario_id', form.id)
          ]);

          // Build all_clients from formulario_clientes join
          const fcRaw = form.formulario_clientes;
          const fcArr = Array.isArray(fcRaw) ? fcRaw : fcRaw ? [fcRaw] : [];
          const allClients = fcArr
            .map((fc: any) => fc.clients)
            .filter(Boolean);

          return {
            ...form,
            client: form.clients || undefined,
            all_clients: allClients,
            total_campos: camposCount.count || 0,
            total_respostas: respostasCount.count || 0
          };
        })
      );

      return formulariosComContagens as Formulario[];
    },
    enabled: !clientLoading,
  });

  // Criar novo formulário
  const addFormulario = useMutation({
    mutationFn: async (formulario: Partial<Formulario>) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { titulo, descricao, client_id, categoria, status, configuracoes } = formulario;
      
      const { data, error } = await supabase
        .from('formularios')
        .insert({
          titulo: titulo || '',
          descricao,
          client_id,
          categoria,
          status: status || 'ativo',
          configuracoes,
          criado_por: user?.id,
          atualizado_por: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Formulário criado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao criar formulário:", error);
      toast.error(error.message || "Erro ao criar formulário");
    },
  });

  // Atualizar formulário
  const updateFormulario = useMutation({
    mutationFn: async ({ id, client_ids, ...updates }: Partial<Formulario> & { id: string; client_ids?: string[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('formularios')
        .update({
          ...updates,
          atualizado_por: user?.id,
          versao: updates.versao ? updates.versao + 1 : 1,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Sync formulario_clientes if client_ids provided
      if (client_ids && client_ids.length > 0) {
        await supabase
          .from('formulario_clientes')
          .delete()
          .eq('formulario_id', id);

        await supabase
          .from('formulario_clientes')
          .insert(client_ids.map(cid => ({
            formulario_id: id,
            client_id: cid,
          })));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Formulário atualizado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao atualizar formulário:", error);
      toast.error(error.message || "Erro ao atualizar formulário");
    },
  });

  // Deletar formulário
  const deleteFormulario = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('formularios')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Formulário deletado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao deletar formulário:", error);
      toast.error(error.message || "Erro ao deletar formulário");
    },
  });

  // Arquivar formulário (soft delete)
  const arquivarFormulario = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('formularios')
        .update({ 
          status: 'arquivado',
          atualizado_por: user?.id,
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Formulário arquivado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao arquivar formulário:", error);
      toast.error(error.message || "Erro ao arquivar formulário");
    },
  });

  // Duplicar formulário
  const duplicarFormulario = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Buscar formulário original
      const { data: original, error: fetchError } = await supabase
        .from('formularios')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Criar novo formulário
      const { data: novoFormulario, error: createError } = await supabase
        .from('formularios')
        .insert({
          titulo: `${original.titulo} (Cópia)`,
          descricao: original.descricao,
          client_id: original.client_id,
          categoria: original.categoria,
          status: 'inativo',
          criado_por: user?.id,
          atualizado_por: user?.id,
          configuracoes: original.configuracoes,
        })
        .select()
        .single();

      if (createError) throw createError;

      // Copiar associações de clientes
      const { data: clientesOriginal } = await supabase
        .from('formulario_clientes')
        .select('client_id')
        .eq('formulario_id', id);

      if (clientesOriginal && clientesOriginal.length > 0) {
        await supabase
          .from('formulario_clientes')
          .insert(clientesOriginal.map((c: any) => ({
            formulario_id: novoFormulario.id,
            client_id: c.client_id,
          })));
      }

      // Buscar e copiar campos
      const { data: campos, error: camposError } = await supabase
        .from('formulario_campos')
        .select('*')
        .eq('formulario_id', id)
        .order('ordem', { ascending: true });

      if (camposError) throw camposError;

      if (campos && campos.length > 0) {
        const camposCopia = campos.map(campo => ({
          formulario_id: novoFormulario.id,
          tipo_campo: campo.tipo_campo,
          label: campo.label,
          descricao: campo.descricao,
          obrigatorio: campo.obrigatorio,
          ordem: campo.ordem,
          configuracoes: campo.configuracoes,
          validacoes: campo.validacoes,
          visibilidade_condicional: campo.visibilidade_condicional,
          ativo: campo.ativo,
        }));

        const { error: insertCamposError } = await supabase
          .from('formulario_campos')
          .insert(camposCopia);

        if (insertCamposError) throw insertCamposError;
      }

      return novoFormulario;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['formularios'] });
      toast.success("Formulário duplicado com sucesso!");
    },
    onError: (error: any) => {
      console.error("Erro ao duplicar formulário:", error);
      toast.error(error.message || "Erro ao duplicar formulário");
    },
  });

  return {
    formularios,
    loading,
    error,
    addFormulario: addFormulario.mutate,
    addFormularioAsync: addFormulario.mutateAsync,
    updateFormulario: updateFormulario.mutate,
    deleteFormulario: deleteFormulario.mutate,
    arquivarFormulario: arquivarFormulario.mutate,
    duplicarFormulario: duplicarFormulario.mutate,
    isAdding: addFormulario.isPending,
    isUpdating: updateFormulario.isPending,
    isDeleting: deleteFormulario.isPending,
  };
}
